//
/* GS://PROTOCOLO
 * Arquivo: src/services/financialDashboardService.js
 * Versão: 5.0 – Motor Final (SKU no Top 5)
 * Impacto da Versão: [Melhoria de dados]
 * Data: 29/11/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - DADOS: Agora captura 'supplierItemCode' ou 'sku' na agregação de produtos.
 * - MANTIDO: Todas as correções anteriores (DRE, Lista Sales, Datas).
 */

import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';

// Helper de Data Seguro
const parseDate = (dateInput) => {
  if (!dateInput) return null;
  if (dateInput.toDate) return dateInput.toDate();
  if (dateInput instanceof Date) return dateInput;
  return new Date(dateInput);
};

export const fetchDashboardData = async (startDate, endDate) => {
  try {
    // Definir intervalo
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay, 0, 0, 0);
    
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
    const end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59);

    // 1. BUSCAR DADOS (Raw Fetch)
    const qSales = query(collection(db, 'sales'), orderBy('createdAt', 'desc'), limit(2000));
    const qLedger = query(collection(db, 'financial_transactions'), orderBy('date', 'desc'), limit(2000));

    const [salesSnap, ledgerSnap] = await Promise.all([
      getDocs(qSales),
      getDocs(qLedger)
    ]);

    // 2. FILTRAGEM EM MEMÓRIA
    const salesInPeriod = [];
    const expensesInPeriod = [];

    salesSnap.forEach(doc => {
      const data = doc.data();
      const dateObj = parseDate(data.createdAt);
      if (dateObj && dateObj >= start && dateObj <= end) {
        salesInPeriod.push({ id: doc.id, ...data, dateObj });
      }
    });

    ledgerSnap.forEach(doc => {
      const data = doc.data();
      if (data.type === 'expense') {
        const dateObj = parseDate(data.date);
        if (dateObj && dateObj >= start && dateObj <= end) {
          expensesInPeriod.push({ id: doc.id, ...data, dateObj });
        }
      }
    });

    // 3. CÁLCULOS
    let kpiRevenue = 0;
    let kpiCount = 0;
    let kpiProducts = 0;
    let kpiPoints = 0;
    let kpiPending = 0;
    const salesByDate = {}; 
    const productsMap = new Map(); 

    let dreGross = 0;
    let dreDeductions = 0;

    salesInPeriod.forEach(sale => {
      const totalPaid = Number(sale.total || sale.valor_total || 0);
      const discount = Number(sale.discount || sale.desconto_aplicado || 0) + 
                       Number(sale.discountManualAmount || 0) +
                       Number(sale.discountPoints || 0);
      const shipping = Number(sale.shipping || sale.frete || 0);
      const paidWithPoints = Number(sale.paidWithPoints || 0);

      // DRE
      if (sale.status !== 'cancelled') {
        dreGross += (totalPaid + discount);
        dreDeductions += discount;
      }

      // KPIs
      kpiRevenue += totalPaid;
      kpiCount++;

      // Status Pendente
      const s = sale.status ? sale.status.toLowerCase() : 'completed';
      const openStatuses = ['pending', 'pendente', 'processing', 'processando', 'preparing', 'shipping'];
      if (openStatuses.includes(s)) kpiPending++;

      // Produtos (Top 5 com SKU)
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach(item => {
           const qty = Number(item.quantity || 1);
           kpiProducts += qty;
           
           // Chave única: ID ou Nome
           const pKey = item.productId || item.name;
           
           if (productsMap.has(pKey)) {
             productsMap.get(pKey).quantity += qty;
           } else {
             productsMap.set(pKey, { 
                 name: item.name, 
                 quantity: qty,
                 // Captura SKU se disponível
                 code: item.supplierItemCode || item.sku || '' 
             });
           }
        });
      }

      // GoldenPoints
      const rawCustomer = sale.customerId || sale.userId;
      const isAnon = !rawCustomer || rawCustomer === 'consumidor-final';
      if (!isAnon) {
        const eligible = Math.max(0, totalPaid - shipping - paidWithPoints);
        kpiPoints += Math.floor(eligible / 25);
      }

      // Gráfico
      const dateKey = sale.dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      if (dateKey) {
        if (!salesByDate[dateKey]) salesByDate[dateKey] = 0;
        salesByDate[dateKey] += totalPaid;
      }
    });

    // Despesas
    let dreExpenses = 0;
    const expenseMap = {};

    expensesInPeriod.forEach(exp => {
        const val = Number(exp.amount || 0);
        dreExpenses += val;
        const cat = exp.categoryName || 'Geral';
        if (!expenseMap[cat]) expenseMap[cat] = 0;
        expenseMap[cat] += val;
    });

    const expenseList = Object.keys(expenseMap)
        .map(key => ({ name: key, value: expenseMap[key] }))
        .sort((a, b) => b.value - a.value);

    const dreNet = dreGross - dreDeductions;
    const dreResult = dreNet - dreExpenses;

    return {
      kpis: {
        totalRevenue: kpiRevenue,
        salesCount: kpiCount,
        productCount: kpiProducts,
        averageTicket: kpiCount > 0 ? kpiRevenue / kpiCount : 0,
        totalGoldenPoints: kpiPoints,
        pendingOrdersCount: kpiPending
      },
      chartData: Object.keys(salesByDate).map(d => ({ date: d, total: salesByDate[d] })).reverse(),
      topProducts: Array.from(productsMap.values()).sort((a, b) => b.quantity - a.quantity).slice(0, 5),
      
      dreStats: {
          grossRevenue: dreGross,
          deductions: dreDeductions,
          netRevenue: dreNet,
          expenses: dreExpenses,
          operatingResult: dreResult,
          expenseBreakdown: expenseList
      },
      sales: salesInPeriod 
    };

  } catch (error) {
    console.error("Erro no motor financeiro robusto:", error);
    throw error;
  }
};